// src/pages/FichePatient.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CONFIG } from '../config/config';
import { renderPrintTemplate } from '../utils/renderPrintTemplate';
import PageLayout from '../components/PageLayout';
import StepIndicator from '../components/StepIndicator';
import CotationOverlay from '../components/CotationOverlay';
import { STATUS_MAP } from '../constants/statusDefinitions';
import { mapDbPatient } from '../utils/patientHelpers';
import { ReactComponent as UrgentIcon } from '../assets/urgent.svg';
import { useSocket } from '../context/SocketContext';
import { useUser } from '../context/UserContext';
import { sheetGet, sheetSyncAndPoll, normalizeCodes as normalizeCodesApi, domainOf } from '../api/cotations';
import { getDisplayMusiqId } from '../utils/musiqId';
import { mklog } from '../utils/debug';
import AddonsBadge from '../components/AddonsBadge';
import { RdvPassageInline, RdvPassageModal } from '../components/RdvPassagePanel';

import {
  ArrowRight,
  Stethoscope,
  BriefcaseMedical,
  Clock3,
  Type,
  Trash2,
  Printer,
  PencilLine,
  Copy,
  Mail,
  ChevronDown,
  FileText,
} from 'lucide-react';
import { calcCotation } from '../utils/calcCotation';
import { listEquipements } from '../api/equipements';

// Profil standard par défaut pour l'auto-calcul
const DEFAULT_STANDARD_QUALIF = {
  profileKey: 'ss_pp100_nrm',
  sS: true,
  specialTP: 'Aucun',
  profileSpecialTPList: ['Aucun'],
  dept: true,
  pctAppDpt: '100',
  pmtBRSS: 'Patient',
  pmtDpt: 'Patient',
  typeFact: 'FSE',
  tp4Partner: [],
  infosCpl: '',
  infoConfirmed: true,
  isInvalid: false,
  requiresCheck: false,
};

async function patchPatientField({ id, token, key, value }) {
  const res = await fetch(`${CONFIG.API_URL}/passages/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ [key]: value }),
  });
  if (!res.ok) throw new Error(`PATCH /passages/${id} -> HTTP ${res.status}`);
  return res.json();
}

function getCodesStringFromPatient(p) {
  const direct = p?.codes || p?.cotationCodes || p?.cotation_codes || p?.cotation_json?.codes || '';
  if (direct)
    return String(direct)
      .toUpperCase()
      .replace(/\s*\+\s*/g, '+');

  try {
    const raw = localStorage.getItem(`musiq:cotation:${p.id}`);
    if (raw) {
      const j = JSON.parse(raw);
      if (j?.codes)
        return String(j.codes)
          .toUpperCase()
          .replace(/\s*\+\s*/g, '+');
    }
  } catch {}

  try {
    const { countedActes, appliedMajos } = calcCotation({
      domaine: p?.domaine || p?.cotationDomaine || undefined,
      actes: Array.isArray(p?.actes) ? p.actes : [],
      majorations: Array.isArray(p?.majorations) ? p.majorations : [],
    });
    const arr = [
      ...countedActes.map((a) => a.code),
      ...(Array.isArray(appliedMajos) ? appliedMajos : [appliedMajos]).filter(Boolean),
    ];
    return arr.join('+').toUpperCase();
  } catch {}
  return '';
}

const useDebounced = (fn, delay = 500) => {
  const t = useRef(null);
  const cb = useCallback(
    (...args) => {
      clearTimeout(t.current);
      t.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay],
  );
  useEffect(() => () => clearTimeout(t.current), []);
  return cb;
};

const STATUS_ICONS = {
  1: Clock3,
  2: BriefcaseMedical,
  3: Clock3,
  4: Stethoscope,
  5: BriefcaseMedical,
  6: BriefcaseMedical,
  7: ArrowRight,
};

function DoctorTimeTracker({ patientId, startTime, maxMinutesTarget = 20 }) {
  const [minutes, setMinutes] = useState(0);

  const [fallbackTime] = useState(() => {
    if (!patientId) return Date.now();
    const storageKey = `timer_medecin_${patientId}`;
    if (startTime) {
      const t = new Date(startTime).getTime();
      localStorage.setItem(storageKey, t);
      return t;
    }
    const stored = localStorage.getItem(storageKey);
    if (stored) return parseInt(stored, 10);
    const now = Date.now();
    localStorage.setItem(storageKey, now.toString());
    return now;
  });

  useEffect(() => {
    const updateDuration = () => {
      const start = startTime ? new Date(startTime).getTime() : fallbackTime;
      if (isNaN(start)) return;
      const now = Date.now();
      const elapsedMs = Math.max(0, now - start);
      setMinutes(Math.floor(elapsedMs / 60000));
    };

    updateDuration();
    const interval = setInterval(updateDuration, 10000);
    return () => clearInterval(interval);
  }, [startTime, fallbackTime]);

  const progressPercent = Math.min(100, Math.round((minutes / maxMinutesTarget) * 100));

  return (
    <div className="mx-4 mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">Temps passé avec le médecin</span>
        <span className="text-sm font-bold text-[#B3000A]">{minutes} min</span>
      </div>
      <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-[#B3000A] transition-all duration-500 rounded-full"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}

export default function FichePatient({ patients = [], setPatients = () => {} }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const socket = useSocket();

  const updatePatientLocal = useCallback(
    (pid, patch) => {
      setPatients((prev) =>
        Array.isArray(prev)
          ? prev.map((p) => (String(p.id) === String(pid) ? { ...p, ...patch } : p))
          : prev,
      );
    },
    [setPatients],
  );

  const [patient, setPatient] = useState(null);
  const [selectedStep, setSelectedStep] = useState(1);
  const [bio, setBio] = useState('non');
  const [isBioUpdating, setIsBioUpdating] = useState(false);
  const [isUrgenceUpdating, setIsUrgenceUpdating] = useState(false);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [equipements, setEquipements] = useState([]);
  const [imprimantesCB, setImprimantesCB] = useState([]);
  const [equipementsLoading, setEquipementsLoading] = useState(false);
  const { etablissement, profile } = useUser();
  const [padEmail, setPadEmail] = useState(null);
  const [printTemplates, setPrintTemplates] = useState([]);

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printTemplatesLoading, setPrintTemplatesLoading] = useState(false);
  const [printCount, setPrintCount] = useState('1');
  const [isPrinting, setIsPrinting] = useState(false);
  const [printError, setPrintError] = useState('');
  const [printSuccess, setPrintSuccess] = useState('');

  const [notes, setNotes] = useState('');
  const prevSentNotesRef = useRef('');
  const notesAbortRef = useRef(null);
  const [notesMeta, setNotesMeta] = useState({
    updatedAt: null,
    updatedBy: null,
  });

  const [showLibre, setShowLibre] = useState(false);
  const [freeText, setFreeText] = useState('');
  const prevSentLibreRef = useRef('');
  const libreAbortRef = useRef(null);
  const [savingFree, setSavingFree] = useState(false);

  const [, setSheetTable] = useState([]);
  const [, setSheetUpdatedAt] = useState(null);

  const [editRdvTime, setEditRdvTime] = useState(false);
  const [editedPassage, setEditedPassage] = useState(null);
  const API = (CONFIG.API_URL || '/api').replace(/\/+$/, '');

  const [showActesMenu, setShowActesMenu] = useState(false);
  const [actesVA, setActesVA] = useState({
    Kalinox: false,
    EBMD: false,
    Attelle: false,
    Plâtre: false,
    'Prise de sang': false,
    'Prélèvement urinaire': false,
  });

  const autoCalculTriggeredRef = useRef(false);

  useEffect(() => {
    const actes = patient?.actes_valeur_ajoutee || patient?.actesValeurAjoutee || {};
    setActesVA({
      Kalinox: Boolean(actes.Kalinox),
      EBMD: Boolean(actes.EBMD),
      Attelle: Boolean(actes.Attelle),
      Plâtre: Boolean(actes.Plâtre),
      'Prise de sang': Boolean(actes['Prise de sang']),
      'Prélèvement urinaire': Boolean(actes['Prélèvement urinaire']),
    });
  }, [patient?.id, patient?.actes_valeur_ajoutee, patient?.actesValeurAjoutee]);

  const handleToggleActe = async (key) => {
    const updated = { ...actesVA, [key]: !actesVA[key] };
    setActesVA(updated);
    if (setPatient) {
      setPatient((prev) => (prev ? { ...prev, actes_valeur_ajoutee: updated } : prev));
    }
    try {
      await patchPatientField({
        id,
        token,
        key: 'actes_valeur_ajoutee',
        value: updated,
      });
    } catch (err) {
      console.error('Erreur sauvegarde acte VA :', err);
    }
  };

  /* ---------------- fetch patient ---------------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${CONFIG.API_URL}/passages/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('Chargement du patient impossible');
        const raw = await r.json();
        const p = mapDbPatient(raw);
        if (!mounted) return;
        setPatient(p);
        if (p.currentStep != null) {
          setSelectedStep(Number(p.currentStep));
        }
        setBio(p.biologie);

        const libre = raw?.cotationLibre || raw?.cotation_libre || '';
        if (typeof libre === 'string') {
          setFreeText(libre);
          prevSentLibreRef.current = libre.trim();
          if (libre) setShowLibre(true);
        }
      } catch (e) {
        setErrorMsg(e.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, token]);

  /* ---------------- AUTO-CALCUL DU CHIFfrage (STANDARD) DES L'ENTREE ---------------- */
  useEffect(() => {
    if (!patient || !token || autoCalculTriggeredRef.current) return;

    const rawCodes = getCodesStringFromPatient(patient);
    const cleanCodes = normalizeCodesApi(rawCodes);

    // On déclenche le calcul automatiquement si des codes existent
    if (cleanCodes) {
      autoCalculTriggeredRef.current = true;
      (async () => {
        try {
          mklog('autoCalcul:start', { id: patient.id, codes: cleanCodes });
          const data = await sheetSyncAndPoll({
            passageId: patient.id,
            codes: cleanCodes,
            token,
            domain: domainOf(patient),
            musiqId: getDisplayMusiqId(patient),
            isDemo: patient.is_demo,
            resQualif: DEFAULT_STANDARD_QUALIF,
          });

          if (Array.isArray(data?.table) && data.table.length) {
            setSheetTable(data.table);
            setSheetUpdatedAt(data?.raw?.timestamp || new Date().toISOString());
            mklog('autoCalcul:done', { id: patient.id, rows: data.table.length });
          }
        } catch (e) {
          console.warn('[FichePatient] Auto-calcul en arrière-plan ignoré/erreur :', e.message);
        }
      })();
    }
  }, [patient, token]);

  useEffect(() => {
    let abort = false;
    (async () => {
      if (!id || !token || !patient?.id) return;
      try {
        const data = await sheetGet(patient.id, token, domainOf(patient));
        if (abort) return;
        if (Array.isArray(data?.table) && data.table.length) {
          setSheetTable(data.table);
          setSheetUpdatedAt(data?.raw?.timestamp || null);
        }
      } catch {}
    })();
    return () => {
      abort = true;
    };
  }, [id, token, patient?.id]);

  /* ---------------- WS updates ---------------- */
  useEffect(() => {
    if (!socket) return;
    const onUpdate = (payload) => {
      if (String(payload.id) !== String(id)) return;
      const norm = mapDbPatient(payload);
      setPatient(norm);
      setSelectedStep(Number(norm.currentStep) || 1);
      setBio(norm.biologie);
      if (typeof norm.commentaire_medecin === 'string') {
        const incoming = String(norm.commentaire_medecin || '').trim();
        prevSentNotesRef.current = incoming;
        setNotes(incoming);
      }
    };
    socket.on('patientUpdated', onUpdate);
    return () => socket.off('patientUpdated', onUpdate);
  }, [socket, id]);

  /* ---------------- notes + meta ---------------- */
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const r = await fetch(`${CONFIG.API_URL}/passages/${id}/commentaire`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const j = await r.json();
          if (!abort) {
            const base = String(j?.content ?? '');
            setNotes(base);
            prevSentNotesRef.current = base.trim();
            setNotesMeta({
              updatedAt: j?.updatedAt ?? null,
              updatedBy: j?.updatedBy ?? null,
            });
          }
        }
      } catch {}
    })();
    return () => {
      abort = true;
    };
  }, [id, token]);

  async function handleRDVChange(id, value) {
    try {
      const res = await fetch(`${API}/passages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date_rdv: value || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const p = mapDbPatient(await res.json());
      setPatients((prev) => prev.map((x) => (x.id === p.id ? p : x)));
    } catch (err) {
      console.error('rdv update error :', err);
      alert('Impossible de modifier le Rendez-vous : ' + err.message);
    }
  }

  function getImprimantesCBOfEtablissement(etablissement, pEquipements) {
    return pEquipements.filter(
      (equipement) =>
        equipement.etablissement_id === etablissement.etablissement_id &&
        equipement.type === 'imprimante-code-barre',
    );
  }
  const refresh = async () => {
    setEquipementsLoading(true);
    try {
      const fequipements = await listEquipements();
      setEquipements(fequipements);
    } catch (e) {
      console.log(e.message);
    } finally {
      setEquipementsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    setImprimantesCB(getImprimantesCBOfEtablissement(etablissement, equipements));
  }, [etablissement, equipements]);

  useEffect(() => {
    const tri = etablissement?.etablissement_trigramme;
    if (!tri) return;
    const tk = sessionStorage.getItem('token') || localStorage.getItem('token');
    fetch(`/api/admin/settings/pad-email/${encodeURIComponent(tri)}`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.email) setPadEmail(data.email);
      })
      .catch(() => {});
  }, [etablissement]);

  /* ---------------- autosave Notes (debounced) ---------------- */
  const saveNotes = useDebounced(async (text) => {
    const trimmed = String(text || '').trim();
    if (trimmed === prevSentNotesRef.current) return;
    const ctrl = new AbortController();
    notesAbortRef.current?.abort();
    notesAbortRef.current = ctrl;
    try {
      mklog('notes:save', { id, len: trimmed.length });
      const res = await fetch(`${CONFIG.API_URL}/passages/${id}/commentaire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: trimmed }),
        signal: ctrl.signal,
      });
      if (res.ok) prevSentNotesRef.current = trimmed;
    } catch {}
  }, 600);

  const handleNotesBlur = async () => {
    const trimmed = String(notes || '').trim();
    if (trimmed === prevSentNotesRef.current) return;
    try {
      notesAbortRef.current?.abort();
      const res = await fetch(`${CONFIG.API_URL}/passages/${id}/commentaire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: trimmed }),
      });
      if (res.ok) prevSentNotesRef.current = trimmed;
    } catch {}
  };

  /* ---------------- autosave Cotation Libre (debounced) ---------------- */
  const saveLibre = useDebounced(async (text) => {
    const trimmed = String(text || '').trim();
    mklog('cotationLibre:save', { id, len: trimmed.length });
    if (trimmed === prevSentLibreRef.current) return;
    setSavingFree(true);
    const ctrl = new AbortController();
    libreAbortRef.current?.abort();
    libreAbortRef.current = ctrl;
    try {
      const res = await fetch(`${CONFIG.API_URL}/passages/${id}/commentaire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: trimmed, field: 'cotation_libre' }),
        signal: ctrl.signal,
      });
      if (res.ok) prevSentLibreRef.current = trimmed;
    } catch {}
    setSavingFree(false);
  }, 600);

  const handleLibreBlur = async () => {
    const trimmed = String(freeText || '').trim();
    if (trimmed === prevSentLibreRef.current) return;
    try {
      libreAbortRef.current?.abort();
      const res = await fetch(`${CONFIG.API_URL}/passages/${id}/commentaire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: trimmed, field: 'cotation_libre' }),
      });
      if (res.ok) prevSentLibreRef.current = trimmed;
    } catch {}
  };

  /* ---------------- actions ---------------- */
  const updateStatus = async (next) => {
    mklog('status:update:begin', { id, next });
    if (!patient) return;
    try {
      const r = await fetch(`${CONFIG.API_URL}/passages/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentStep: next, profileId: profile?.profile_id }),
      });
      if (r.ok) {
        const u = await r.json();
        setPatient(u);
        updatePatientLocal(id, u);
        setSelectedStep(Number(u.currentStep) || next);
        mklog('status:update:done', { id, currentStep: Number(u.currentStep) || next });
      }
    } catch {
      setErrorMsg('Impossible de changer le statut');
    }
  };

  const toggleUrgence = async () => {
    if (!patient) return;
    setIsUrgenceUpdating(true);
    try {
      const r = await fetch(`${CONFIG.API_URL}/passages/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ urgence: !patient.urgence }),
      });
      if (r.ok) {
        const u = await r.json();
        setPatient(u);
        updatePatientLocal(id, u);
      }
    } finally {
      setIsUrgenceUpdating(false);
    }
  };

  const updateBiologie = async (next) => {
    mklog('bio:update', { id, next });
    if (!patient || isBioUpdating) return;

    setBio(next);
    setIsBioUpdating(true);
    try {
      const r = await fetch(`${CONFIG.API_URL}/passages/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ biologie: next }),
      });
      if (r.ok) {
        const u = await r.json();
        setPatient(u);
        updatePatientLocal(id, u);
      }
    } finally {
      setIsBioUpdating(false);
    }
  };

  const deplacerPatient = async (salle) => {
    try {
      const r = await fetch(`${CONFIG.API_URL}/passages/${id}/salle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          salle: salle || null,
          currentStep: Number(patient?.currentStep ?? selectedStep) || 1,
        }),
      });
      if (r.ok) {
        const u = await r.json();
        setPatient(u);
        updatePatientLocal(id, u);
        setSelectedStep((s) => Number(u?.currentStep ?? s));
      }
    } catch {}
  };

  const clearFree = async () => {
    setFreeText('');
  };

  const loadPrintTemplates = async () => {
    if (!token) return;
    setPrintTemplatesLoading(true);
    try {
      const r = await fetch(`${CONFIG.API_URL}/print-code-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setPrintTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('loadPrintTemplates failed', e);
      setPrintTemplates([]);
    } finally {
      setPrintTemplatesLoading(false);
    }
  };

  const openPrintModal = () => {
    setPrintError('');
    setPrintSuccess('');
    setPrintCount('1');
    setIsPrintModalOpen(true);
    loadPrintTemplates();
  };

  const closePrintModal = () => {
    if (isPrinting) return;
    setIsPrintModalOpen(false);
  };

  const openScanFromTemplate = (t) => {
    if (!patient) return;
    const payload = { ...patient, musiqId: getDisplayMusiqId(patient) };
    const value = renderPrintTemplate(t.template, payload);
    navigate('/scan', {
      state: {
        name: t.name,
        kind: t.kind,
        value,
      },
    });
    setIsPrintModalOpen(false);
  };

  const handlePrintLabels = async (printer) => {
    if (!patient) return;
    const count = Math.max(1, Math.min(50, Number(printCount) || 1));

    setIsPrinting(true);
    setPrintError('');
    setPrintSuccess('');
    try {
      const pentagramme = profile?.profile_pentagramme || 'SANS';
      const res = await fetch(`${CONFIG.API_URL}/print/biology-labels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          passageId: patient.id,
          count,
          printerId: printer.id,
          pentagramme: pentagramme,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      setPrintSuccess('✅ Impression lancée');
      closePrintModal();
    } catch (err) {
      console.error(err);
      setPrintError('Erreur lors de l’envoi à l’imprimante');
    } finally {
      setIsPrinting(false);
    }
  };

  /* ---------------- derived ---------------- */
  const currentStatus = useMemo(() => STATUS_MAP.get(Number(selectedStep)) || null, [selectedStep]);
  const StatusIcon = STATUS_ICONS[selectedStep] || Clock3;
  const canCoter = selectedStep === 4 || selectedStep === 7;

  const ddn = patient
    ? new Date(patient.dateNaissance || patient.ddn || patient.date_naissance).toLocaleDateString(
        'fr-FR',
        { day: 'numeric', month: 'long', year: 'numeric' },
      )
    : '';

  useEffect(() => {
    if (!socket || !id) return;
    const onReady = (p) => {
      if (String(p?.passageId) !== String(id)) return;
      if (Array.isArray(p?.table) && p.table.length) {
        setSheetTable(p.table);
        setSheetUpdatedAt(p.updatedAt || new Date().toISOString());
        const codesUp = p?.codes || getCodesStringFromPatient(patient || {});
        setPatient((prev) => ({
          ...prev,
          cotation_status: 'complete',
          cotationStatus: 'complete',
          codes: codesUp || prev?.codes,
          cotationCodes: codesUp || prev?.cotationCodes,
          cotation_json: {
            ...(prev?.cotation_json || {}),
            codes: codesUp || prev?.cotation_json?.codes,
            table: p.table,
          },
        }));
        updatePatientLocal(id, {
          cotation_status: 'complete',
          cotationStatus: 'complete',
          codes: codesUp,
          cotationCodes: codesUp,
        });
        setOpenOverlay(false);
        return;
      }
      sheetGet(patient.id, token, domainOf(patient))
        .then((data) => {
          if (Array.isArray(data?.table) && data.table.length) {
            setSheetTable(data.table);
            setSheetUpdatedAt(data?.raw?.timestamp || new Date().toISOString());
            const codesUp = p?.codes || getCodesStringFromPatient(patient || {});
            setPatient((prev) => ({
              ...prev,
              cotation_status: 'complete',
              cotationStatus: 'complete',
              codes: codesUp || prev?.codes,
              cotationCodes: codesUp || prev?.cotationCodes,
              cotation_json: {
                ...(prev?.cotation_json || {}),
                codes: codesUp || prev?.cotation_json?.codes,
                table: data.table,
              },
            }));
            updatePatientLocal(id, {
              cotation_status: 'complete',
              cotationStatus: 'complete',
              codes: codesUp,
              cotationCodes: codesUp,
            });
            setOpenOverlay(false);
          }
        })
        .catch(() => {});
    };
    socket.on('cotationSheetReady', onReady);
    return () => socket.off('cotationSheetReady', onReady);
  }, [socket, id, token, updatePatientLocal, patient]);

  const AnimCSS = () => (
    <style>{`
      @keyframes flash { 0%,100%{opacity:.25} 50%{opacity:1} }
      .animate-ff1 { animation: flash 1.8s infinite .0s; }
      .animate-ff2 { animation: flash 1.8s infinite .25s; }
      .animate-ff3 { animation: flash 1.8s infinite .5s; }
    `}</style>
  );

  const TONES = {
    red: { text: 'text-red-600', hover: 'hover:bg-red-50/70', ring: 'ring-red-300' },
    emerald: {
      text: 'text-emerald-600',
      hover: 'hover:bg-emerald-50/70',
      ring: 'ring-emerald-300',
    },
    slate: { text: 'text-slate-600', hover: 'hover:bg-slate-50/70', ring: 'ring-slate-300' },
  };

  function getSalleNomFromId(id, salles) {
    return id == null ? '-' : (salles?.find((s) => s.salle_id === id)?.salle_nom ?? '-');
  }

  const toneForStep = (step) => {
    if ([3, 4].includes(step)) return TONES.red;
    if ([1, 2, 5, 6].includes(step)) return TONES.emerald;
    return TONES.slate;
  };

  const FastForward = ({ className = '' }) => (
    <span className={`relative inline-block h-6 w-7 ${className}`} aria-hidden>
      <svg className="absolute left-0 top-0 h-5 w-5 opacity-50 animate-ff1" viewBox="0 0 24 24" fill="none">
        <path d="M6 4l8 8-8 8V4z" fill="currentColor" />
      </svg>
      <svg className="absolute left-2 top-1 h-5 w-5 opacity-60 animate-ff2" viewBox="0 0 24 24" fill="none">
        <path d="M6 4l8 8-8 8V4z" fill="currentColor" />
      </svg>
      <svg className="absolute left-4 top-2 h-5 w-5 opacity-90 animate-ff3" viewBox="0 0 24 24" fill="none">
        <path d="M6 4l8 8-8 8V4z" fill="currentColor" />
      </svg>
    </span>
  );

  const NextStep = () => {
    const currentStatus = STATUS_MAP.get(Number(selectedStep)) || null;
    if (!currentStatus?.next) return null;
    const nexts = Array.isArray(currentStatus.next) ? currentStatus.next : [currentStatus.next];
    const renderBtn = (step) => {
      if (step > 7) return null;
      const nextName = STATUS_MAP.get(step)?.name || 'Étape suivante';
      const { text, hover, ring } = toneForStep(step);
      return (
        <button
          key={step}
          onClick={() => updateStatus(step)}
          className={[
            'group relative mx-auto flex w-full max-w-xl items-center justify-between gap-4',
            'rounded-2xl bg-white/80 px-6 py-5 backdrop-blur shadow-sm',
            'active:scale-[.99] transition ring-1',
            ring,
            hover,
          ].join(' ')}
        >
          <div className="flex items-center text-slate-800">
            <FastForward className={['mr-2', text].join(' ')} />
            <span className="text-[15px]">
              → Vers <span className="font-semibold">{nextName}</span>
            </span>
          </div>
          <svg className="h-5 w-5 text-slate-400 group-hover:text-slate-600" viewBox="0 0 24 24" fill="none">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      );
    };
    return <div className="mt-6 mb-8 flex flex-col items-center gap-4 px-5">{nexts.map(renderBtn)}</div>;
  };

  /* ---------------- render ---------------- */
  if (!patient) {
    return (
      <PageLayout title="Fiche patient">
        <div className="p-6">Chargement…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Fiche patient">
      <AnimCSS />

      <RdvPassageModal
        passage={editedPassage}
        onConfirm={(id, datetime) => handleRDVChange(id, datetime)}
        onClose={() => {
          setEditedPassage(null);
          setEditRdvTime(false);
        }}
      />

      {/* Bandeau salle / statut */}
      <div className="px-2 sm:px-5">
        <div
          className="w-full rounded-2xl bg-gradient-to-r from-[#B3000A] to-[#E1141C]
               text-white px-3 py-2.5 sm:px-6 sm:py-4
               flex flex-row items-center justify-between gap-2 sm:gap-4"
        >
          <div className="text-lg sm:text-3xl font-semibold truncate">
            Salle {getSalleNomFromId(patient.salle_id, etablissement?.salles)}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="text-right text-xs sm:text-sm font-medium">
              {currentStatus?.name || '—'}
            </div>
            <div className="grid h-8 w-8 sm:h-9 sm:w-9 place-items-center rounded-full bg-white/15">
              <StatusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div
        className={[
          'mx-2 sm:mx-4 mt-2 sm:mt-3 rounded-2xl border bg-white px-3 sm:px-4 py-3',
          patient?.urgence ? 'ring-1 ring-red-200 bg-red-50/70' : 'border-slate-200',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <button
              onClick={toggleUrgence}
              disabled={isUrgenceUpdating}
              aria-label="Urgence"
              className="shrink-0 p-0.5"
            >
              <UrgentIcon
                className={[
                  'h-7 w-7',
                  patient?.urgence ? 'text-[#B3000A]' : 'text-[#B3000A] opacity-30',
                ].join(' ')}
              />
            </button>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight truncate">
                  {patient.civilite} {String(patient.nom || '').toUpperCase()}{' '}
                  {patient.prenom || ''}
                </h1>
                {patient.urgence && (
                  <span className="inline-flex items-center rounded-full bg-[#B3000A] px-2 py-0.5 text-[10px] font-bold text-white">
                    URGENCE
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-sm text-slate-700">{ddn}</div>
              {getDisplayMusiqId(patient) && (
                <div className="font-mono text-[12px] text-slate-500">
                  {getDisplayMusiqId(patient)}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <span className="font-semibold text-slate-600">RDV : </span>
                <RdvPassageInline
                  p={patient}
                  onEdit={(passage) => {
                    setEditedPassage(passage);
                    setEditRdvTime(true);
                  }}
                  onClear={(id) => handleRDVChange(id, null)}
                />
              </div>
            </div>
          </div>

          <div className="hidden sm:flex shrink-0 items-center gap-2">
            <AddonsBadge
              bio={bio}
              onChangeBio={(n) => updateBiologie(n)}
              disabledBio={isBioUpdating}
              patient={patient}
              onPrintClick={openPrintModal}
              padEmail={padEmail}
            />
          </div>
        </div>

        <div className="mt-2 sm:hidden flex items-center gap-2">
          <AddonsBadge
            bio={bio}
            onChangeBio={(n) => updateBiologie(n)}
            disabledBio={isBioUpdating}
            patient={patient}
            onPrintClick={openPrintModal}
            padEmail={padEmail}
          />
        </div>
      </div>

      {Number(selectedStep) === 4 && (
        <DoctorTimeTracker
          patientId={patient?.id}
          startTime={patient?.h_etape_4}
          maxMinutesTarget={20}
        />
      )}

      {/* Steps */}
      <div className="px-2 sm:px-4 py-2 sm:py-3">
        <div className="px-2 sm:px-4 pb-2 pt-2 sm:pt-4">
          <div className="mx-auto max-w-4xl">
            <StepIndicator
              steps={[1, 2, 3, 4, 5, 6]}
              current={Number(selectedStep) || 1}
              size="sm"
              variant="auto"
              onStepClick={(s) => updateStatus(s)}
              desktopFull
              colorMap={{ infirmiere: [1, 2, 5, 6], medecin: [3, 4] }}
            />
          </div>
        </div>

        {canCoter && (
          <div className="mt-6 px-5">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setOpenOverlay(true)}
                className={
                  'flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-r px-5 py-4 font-semibold shadow active:scale-[.98]' +
                  (patient.isSheetsCalculating
                    ? ' cursor-not-allowed from-gray-300 to-gray-300 text-gray-400'
                    : ' from-red-700 to-red-600 text-white')
                }
                title="Assistant cotation"
                disabled={patient.isSheetsCalculating}
              >
                <PencilLine className="h-5 w-5" />
                {patient?.cotationStatus === 'complete' || patient?.cotation_status === 'complete'
                  ? 'Modifier la cotation'
                  : 'Ouvrir l’assistant'}
              </button>

              <button
                onClick={() => setShowLibre((s) => !s)}
                className={`flex items-center justify-center gap-3 rounded-2xl px-5 py-4 font-semibold shadow active:scale-[.98] ${
                  showLibre
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 bg-white text-slate-900 hover:border-slate-300'
                }`}
                title="Cotation libre"
                aria-label="Cotation libre"
              >
                <Type className="h-5 w-5" />
                Cotation libre
              </button>
            </div>

            <div className="h-4" />

            {showLibre && (
              <div className="col-span-2 mt-2">
                <div className="relative">
                  <textarea
                    rows={2}
                    id="cotation-libre"
                    value={freeText}
                    onBlur={handleLibreBlur}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFreeText(v);
                      saveLibre(v);
                    }}
                    placeholder="Saisir la cotation manuelle si besoin (remplace l’assistant)"
                    className="w-full rounded-xl border p-3 pr-10 text-sm"
                  />
                  <button
                    type="button"
                    onClick={clearFree}
                    title="Effacer"
                    className="absolute right-2 top-2.5 rounded p-1 hover:bg-gray-100"
                  >
                    <Trash2 size={18} />
                  </button>
                  {savingFree && (
                    <div className="absolute right-2 -bottom-5 text-xs text-gray-500">
                      Enregistrement…
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <NextStep />
      </div>

      {/* Codes de cotation (affichés dès qu'ils existent) */}
      {(() => {
        const codes = getCodesStringFromPatient(patient);
        if (!codes) return null;
        return (
          <div className="mt-4 px-5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-red-700/80">
              Codes
            </div>
            <div className="mt-1 inline-flex items-center gap-2 bg-red-50 text-red-900 px-3 py-2 rounded">
              <span className="font-mono break-all">{codes}</span>
              <button
                onClick={() =>
                  navigator.clipboard
                    .writeText(codes.replace(/\s*\+\s*/g, '+'))
                    .then(() => console.debug('Codes copiés'))
                }
                className="p-1 rounded hover:bg-red-100"
                aria-label="Copier"
                title="Copier"
              >
                <Copy className="h-5 w-5 text-red-700" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Actes à valeur ajoutée & Arrêt de travail */}
      <div className="mt-6 sm:mt-10 border-t border-slate-200 pt-4 sm:pt-6">
        <div className="px-2 sm:px-4 pb-3 flex flex-col sm:flex-row justify-between items-start gap-3">
          <div className="relative inline-block text-left w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setShowActesMenu((prev) => !prev)}
              className="inline-flex items-center justify-between w-full sm:w-64 gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#E1141C]"
            >
              <div className="flex items-center gap-2">
                <span>Actes à valeur ajoutée</span>
                {Object.values(actesVA).filter(Boolean).length > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#B3000A] text-xs font-bold text-white shadow-sm">
                    {Object.values(actesVA).filter(Boolean).length}
                  </span>
                )}
              </div>
              <ChevronDown
                className={`h-4 w-4 text-slate-500 transition-transform ${showActesMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {showActesMenu && (
              <div className="mt-2 w-full sm:w-64 rounded-xl border border-slate-200 bg-white p-3 shadow-lg z-20">
                <div className="space-y-2">
                  {[
                    'Kalinox',
                    'EBMD',
                    'Attelle',
                    'Plâtre',
                    'Prise de sang',
                    'Prélèvement urinaire',
                  ].map((item) => (
                    <div
                      key={item}
                      onClick={() => handleToggleActe(item)}
                      className="flex items-center gap-3 p-1.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(actesVA[item])}
                        onChange={() => {}}
                        className="h-4 w-4 rounded border-slate-300 text-[#B3000A] focus:ring-[#E1141C] pointer-events-none"
                      />
                      <span className="font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate(`/arret-travail/${patient.id}`)}
            className="inline-flex items-center justify-center w-full sm:w-auto gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-[#B3000A] hover:border-[#B3000A] focus:outline-none focus:ring-2 focus:ring-[#E1141C] transition-colors"
            title="Ouvrir le parcours Arrêt de travail"
          >
            <FileText className="h-4 w-4" />
            Arrêt de travail
          </button>
        </div>

        {/* Notes */}
        <div className="px-2 sm:px-4 pb-4">
          <div className="mb-1 flex items-center justify-between">
            <label className="text-sm font-medium">Notes</label>
            {notesMeta?.updatedAt && (
              <span className="text-[11px] text-gray-500">
                Modifié le {new Date(notesMeta.updatedAt).toLocaleString('fr-FR')}
              </span>
            )}
          </div>
          <textarea
            rows={4}
            placeholder="Notes libres (visible par tous)"
            className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E1141C]"
            value={notes}
            onBlur={handleNotesBlur}
            onChange={(e) => {
              const v = e.target.value;
              setNotes(v);
              saveNotes(v);
            }}
          />
        </div>

        {/* Salle */}
        <div className="px-2 sm:px-4 pb-2">
          <div className="rounded-xl border bg-white">
            <div className="px-3 pb-1 pt-2 text-xs font-medium text-slate-500">Salle</div>

            <div className="flex flex-wrap sm:flex-nowrap gap-2 px-2 pb-3">
              <button
                onClick={() => deplacerPatient('')}
                className={`flex-none min-w-[72px] sm:flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${
                  !patient.salle_id
                    ? 'border-red-600 bg-red-600 text-white shadow'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Aucune
              </button>

              {etablissement?.salles
                ?.slice()
                .sort((a, b) => a.salle_nom.localeCompare(b.salle_nom))
                .map((s) => (
                  <button
                    key={s.salle_id}
                    onClick={() => deplacerPatient(s.salle_id)}
                    className={`flex-none min-w-[72px] sm:flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${
                      patient.salle_id === s.salle_id
                        ? 'border-red-600 bg-red-600 text-white shadow'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {s.salle_nom}
                  </button>
                ))}
            </div>
          </div>

          {patient.salle_id && (
            <div className="mt-2 text-xs text-gray-600">
              <div className="font-medium">
                Déjà présents en salle {getSalleNomFromId(patient.salle_id, etablissement?.salles)} :
              </div>
              <ul className="list-inside list-disc">
                {patients
                  .filter(
                    (x) =>
                      x.id !== patient.id &&
                      x.salle_id === patient.salle_id &&
                      Number(x.currentStep) >= 1 &&
                      Number(x.currentStep) <= 6,
                  )
                  .map((p) => (
                    <li key={p.id}>
                      {p.civilite} {p.prenom} {String(p.nom || '').toUpperCase()}
                    </li>
                  ))}
                {patients.filter(
                  (x) =>
                    x.id !== patient.id &&
                    x.salle_id === patient.salle_id &&
                    Number(x.currentStep) >= 1 &&
                    Number(x.currentStep) <= 6,
                ).length === 0 && <li>Aucun autre patient</li>}
              </ul>
            </div>
          )}
        </div>
      </div>

      {errorMsg && <div className="px-4 pb-4 text-center text-sm text-red-600">⚠️ {errorMsg}</div>}

      {/* Modale impression étiquettes */}
      {isPrintModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-[90vw] md:max-w-[60vw] rounded-2xl bg-white p-4 shadow-lg">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Impression étiquettes</h2>
            <p className="text-xs text-slate-600 mb-3">
              {patient.civilite} {patient.prenom} <span className="uppercase">{patient.nom}</span>
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-2 py-2">
                <div className="flex-1">
                  <div className="font-medium">Étiquettes de biologie</div>
                </div>
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="mr-1">Quantité:</label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={printCount}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          setPrintCount('');
                          return;
                        }
                        const n = Number(raw);
                        if (Number.isNaN(n)) return;
                        if (n < 1) setPrintCount('1');
                        else if (n > 50) setPrintCount('50');
                        else setPrintCount(raw);
                      }}
                      onBlur={() => {
                        const n = Math.max(1, Math.min(50, Number(printCount) || 1));
                        setPrintCount(String(n));
                      }}
                      className="w-14 rounded-md border border-slate-300 px-2 py-1 text-xs text-right"
                    />
                  </div>

                  {equipementsLoading ? (
                    <p className="text-sm text-gray-500">Chargement…</p>
                  ) : imprimantesCB.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Aucune imprimante codes barres configurée
                    </p>
                  ) : (
                    <>
                      {imprimantesCB.map((equipement) => (
                        <button
                          key={equipement.id}
                          type="button"
                          onClick={() => handlePrintLabels(equipement)}
                          disabled={isPrinting}
                          className="w-full inline-flex items-center justify-center gap-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                        >
                          <Printer className="h-3 w-3" />
                          {equipement.libelle}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div className="mt-2">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Codes écran
                </div>

                {printTemplatesLoading ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600">
                    Chargement des codes…
                  </div>
                ) : printTemplates.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600">
                    Aucun code configuré (admin).
                  </div>
                ) : (
                  <div className="space-y-2">
                    {printTemplates.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-2 py-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{t.name}</div>
                          <div className="text-[11px] text-slate-500">
                            {t.kind === 'qr' ? 'QR 2D' : 'Code-barres 1D (Code128)'}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => openScanFromTemplate(t)}
                          className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
                        >
                          Afficher
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {printError && <p className="mt-2 text-xs text-red-600">{printError}</p>}
            {printSuccess && <p className="mt-2 text-xs">{printSuccess}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closePrintModal}
                disabled={isPrinting}
                className="px-3 py-1 rounded-md border border-slate-300 text-xs text-slate-700 hover:bg-slate-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <CotationOverlay
        open={openOverlay}
        onClose={() => setOpenOverlay(false)}
        passageId={id}
        patients={patients}
        setPatients={setPatients}
        token={token}
        currentPage="fichePatient"
      />
    </PageLayout>
  );
}